import type {DashboardDetail, WidgetConfig} from 'src/types/byod';
import {DASHBOARD_STATUS, ROW_TYPE, WIDGET_TYPE} from 'src/types/byod';
import type {InitDashboardParams} from 'src/components/analytics-genesis/byod/store/dashboardStore';
import {captureSentryMessage} from 'src/utils/sentry';

// ---------------------------------------------------------------------------
// Hydration: GET /dashboards/:id response → InitDashboardParams
// ---------------------------------------------------------------------------
// Preserves all placements — including those whose widget_def was deleted from
// the catalog. Deleted-def placements use fallback metadata so the widget
// config (filters, config JSON) is never lost. The UI shows an informational
// banner when orphaned widgets are detected.
//
// Every dashboard loaded from the backend is implicitly published — the
// server-side draft concept was removed. We hardcode status=PUBLISHED here so
// the view-vs-edit gating (canEdit) treats read-mode correctly regardless of
// whether the backend still sends a `status` field.

export type HydrationResult = {
  params: InitDashboardParams;
  /** Number of placements whose catalog definition was deleted. */
  orphanedWidgetCount: number;
};

export function hydrateDashboardFromApi(
  data: DashboardDetail,
): HydrationResult {
  const allPlacements = Object.entries(data.widgets_config);
  const orphanedWidgetCount = allPlacements.filter(
    ([, p]) => p.widget_def == null,
  ).length;

  if (orphanedWidgetCount > 0) {
    const orphanedKeys = allPlacements
      .filter(([, p]) => p.widget_def == null)
      .map(([id, p]) => ({placementId: id, widgetKey: p.widget_key}));
    captureSentryMessage(
      'BYOD hydration found placements with deleted widget definitions',
      {
        level: 'warning',
        tags: {feature: 'byod', context: 'hydration'},
        extra: {dashboardId: data.id, orphanedKeys, orphanedWidgetCount},
      },
    );
  }

  const rows = (data.layouts ?? []).filter(
    (row) => row.tiles.length > 0 || row.type === ROW_TYPE.SECTION,
  );

  const widgets: Record<string, WidgetConfig> = Object.fromEntries(
    allPlacements.map(([placementId, p]) => {
      const def = p.widget_def;
      // Extract per-placement metadata stored inside widget_config by
      // buildSaveAndPublishPayload, then strip them so widget_config_json
      // only contains runtime query params (pagination, sort, chart_style).
      const rawConfig = (p.widget_config ?? {}) as Record<string, unknown>;
      const {
        label: cfgLabel,
        widget_icon: cfgIcon,
        widget_type: cfgType,
        ...cleanConfig
      } = rawConfig;
      return [
        placementId,
        {
          widget_id: placementId,
          widget_type:
            (cfgType as WidgetConfig['widget_type'] | undefined) ??
            def?.widget_type ??
            WIDGET_TYPE.KPI,
          widget_key: p.widget_key,
          label: (cfgLabel as string | undefined) ?? def?.name ?? p.widget_key,
          widget_icon:
            (cfgIcon as string | undefined) ??
            def?.widget_icon ??
            'chart-mixed',
          widget_filters_json: p.widget_filters,
          widget_config_json: cleanConfig,
        },
      ];
    }),
  );

  return {
    params: {
      id: data.id,
      name: data.name,
      description: data.description ?? '',
      createdById: data.created_by?.id ?? null,
      rowOrder: rows.map((r) => r.id),
      rows: Object.fromEntries(rows.map((r) => [r.id, r])),
      widgets,
      status: DASHBOARD_STATUS.PUBLISHED,
    },
    orphanedWidgetCount,
  };
}
